import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { applyAppearance, readAppearance, type Appearance } from "./appearance";
import type { AlertConfig } from "../core/alerts";
import type { SimulationOverrides } from "../shared/types";
import { DEFAULT_SPEECH_VOICE, type SpeechVoice } from "../shared/speech-catalogue";

export interface Preferences extends Appearance {
	/** Route selected in Alerts and used as the app default. */
	defaultProfileId: string | null;
	/** Preferred arrival time at the final destination, minutes after local midnight. */
	targetMinutes: number | null;
	earlyToleranceMinutes: number | null;
	lateToleranceMinutes: number | null;
	/** Days the commute runs, mirrored from the alert config. GTFS indices, 0 = Monday. */
	targetDays: number[] | null;
	/** Platform to the real destination, e.g. Rockridge to school. */
	egressSeconds: number | null;
	/** Bathroom, keys, walk to the car. */
	prepSeconds: number | null;
	stationBufferSeconds: number | null;
	/** Play a bell and spoken reminder in the app when it is time to leave. */
	soundEnabled: boolean;
	/** Which of the two generated voices speaks the reminders. */
	speechVoice: SpeechVoice;
	/** Speech synthesis voice used for the in-app reminder. Null uses the system default. */
	voiceURI: string | null;
	/** Where the drive starts, mirrored from the alert config. The app has no other source. */
	origin: { lat: number; lon: number } | null;
	/** Scale for everything except the hero card. Home Screen Safari has no pinch zoom. */
	textScale: number;
	/** How hard the iOS app's buzzes land, 0 to 1. Ignored in a browser, which cannot buzz. */
	hapticIntensity: number;
	/** Draw the lock screen and Dynamic Island in Settings, so they can be designed without a rebuild. */
	activityPreview: boolean;
	debug: boolean;
	simulation: SimulationOverrides | null;
}

const STORAGE_KEY = "gotime.preferences.v2";

const DEFAULTS: Preferences = {
	colorMode: "dark",
	liquidGlass: true,
	defaultProfileId: null,
	targetMinutes: null,
	earlyToleranceMinutes: null,
	lateToleranceMinutes: null,
	targetDays: null,
	egressSeconds: null,
	prepSeconds: null,
	stationBufferSeconds: null,
	soundEnabled: true,
	speechVoice: DEFAULT_SPEECH_VOICE,
	voiceURI: null,
	origin: null,
	// Sized for a phone held at arm's length; the 100% layout is the compact one.
	textScale: 1.3,
	// Full strength reads as an alarm for something as ordinary as declaring a phase.
	hapticIntensity: 0.7,
	activityPreview: false,
	debug: false,
	simulation: null,
};

export function readPreferences(): Preferences {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return DEFAULTS;
		}
		const saved = JSON.parse(raw) as Partial<Preferences>;
		return { ...DEFAULTS, ...saved, ...readAppearance(saved) };
	} catch {
		return DEFAULTS;
	}
}

/**
 * What to take from the alert the server is holding.
 *
 * The alert settings live in two places: here, for the view, and in the subscription, for
 * the cron that runs while the phone is asleep. The subscription is only ever a starting point
 * — a value chosen on this device always wins, or opening the app would quietly undo the last
 * thing the rider changed.
 */
export function adoptedAlertDefaults(preferences: Preferences, config: AlertConfig): Partial<Preferences> {
	const patch: Partial<Preferences> = {};
	if (preferences.targetMinutes == null) {
		patch.targetMinutes = config.targetMinutes;
	}
	if (preferences.targetDays == null) {
		patch.targetDays = config.days;
	}
	for (const key of ["earlyToleranceMinutes", "lateToleranceMinutes", "egressSeconds", "prepSeconds", "stationBufferSeconds"] as const) {
		if (preferences[key] == null && config[key] != null) {
			patch[key] = config[key];
		}
	}
	return patch;
}

export function usePreferences(): [Preferences, (patch: Partial<Preferences>) => void] {
	const [preferences, setPreferences] = useState<Preferences>(readPreferences);

	useLayoutEffect(() => {
		applyAppearance(preferences);
	}, [preferences.colorMode, preferences.liquidGlass]);

	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
		} catch {
			// Private browsing or a full quota is not a reason to break the app.
		}
	}, [preferences]);

	const update = useCallback((patch: Partial<Preferences>) => {
		setPreferences((current) => ({ ...current, ...patch }));
	}, []);

	return [preferences, update];
}
